import { Form, InlineMessage, Select, TextField } from '@boltenergy-be/design-system'
import { useTranslation } from 'react-i18next'
import styles from '../Details.module.scss'
import { SimulationDetailsProps } from '../types'
import DataBlock from 'features/contracts/add/components/data-block/DataBlock'
import { Opportunity } from 'types/contacts'
import { useEffect, useState } from 'react'
import { Town } from 'lib/types'
import { getTown } from 'lib/addContract'
import { isValidPostalCode } from 'utils/addContract'
import { useFormContext } from 'react-hook-form'
import { Toggle } from '@boltenergy-be/design-system'
import { PAYMENT_METHODS } from '@boltenergy-be/shared'
import { AddressWithCountryAndTownCode } from 'types/general.ts'
import { Iban } from 'components/form-fields'

const EditSimulationDetails = ({ opportunity }: SimulationDetailsProps) => {
  // i18n
  const { t } = useTranslation(['sales', 'validation', 'common'])

  // React hookform
  const hookform = useFormContext<Partial<Omit<Opportunity, 'address'> & { address: AddressWithCountryAndTownCode }>>()
  const {
    register,
    setValue,
    watch,
    formState: { errors }
  } = hookform
  const watchTownName = watch('address.townName')
  const watchPaymentMode = watch('paymentMode')

  // Local state
  const [selectableTowns, setSelectableTowns] = useState<Town[]>([])

  // Constants
  const ibanIsRequired = watchPaymentMode === PAYMENT_METHODS.domiciliation

  // Set town from postcode on page load
  useEffect(() => {
    void validatePostalCodeAndFetchTowns(
      typeof opportunity.address.postalCode === 'string' ? parseInt(opportunity.address.postalCode) : opportunity.address.postalCode
    )
  }, [])

  /**
   * Handles the town select change & sets the town code
   *
   * @param {string} selectedTown
   */
  const handleTownSelectChange = (selectedTown: string) => {
    const town = selectableTowns.find((town) => town.townName === selectedTown)
    const townCode = town?.townCode ?? 0
    const townName = town?.townName ?? ''

    setValue('address.townCode', townCode, { shouldDirty: true })
    setValue('address.townName', townName, { shouldDirty: true })
  }

  /**
   * Fetches the town names based on the given postalCode
   *
   * @param {number} postalCode
   */
  const validatePostalCodeAndFetchTowns = async (postalCode: number) => {
    if (isValidPostalCode(postalCode)) {
      const response = await getTown(postalCode)

      if (response !== null) {
        const { towns } = response
        setSelectableTowns(towns)

        if (!towns.some((town) => town.townName === watchTownName)) {
          setValue('address.townName', towns[0].townName, { shouldDirty: true })
        }
        setValue('address.townCode', towns[0].townCode, { shouldDirty: true })
        return true
      }
    }

    return t(($) => $.invalid.postalCode, {
      ns: 'validation'
    })
  }

  return (
    <Form>
      <TextField
        type="number"
        label={t(($) => $.details.simulation.pricePerMonth)}
        name="pricePerMonth"
        defaultValue={opportunity.pricePerMonthInclVat}
        disabled
      />

      <TextField
        type="number"
        label={t(($) => $.details.simulation.forcedAmount)}
        inputMode="numeric"
        {...register('forcedAmount', {
          required: false,
          max: {
            value: 10_000,
            message: t(($) => $.tooHigh, {
              max: 10_000,
              ns: 'validation'
            })
          },
          min: {
            value: opportunity.pricePerMonthInclVat,
            message: t(($) => $.tooLow, {
              min: opportunity.pricePerMonthInclVat,
              ns: 'validation'
            })
          }
        })}
        error={errors?.forcedAmount?.message}
      />

      <span className="grid-col-full">
        <DataBlock label={t(($) => $.details.simulation.paymentMethod)}>
          <Toggle
            options={Object.values(PAYMENT_METHODS).map((value) => ({
              label: t(($) => $.details.simulation.paymentMethodOptions[value]),
              value: value
            }))}
            onClick={(value) => setValue('paymentMode', value)}
            active={watchPaymentMode}
          />
          {errors?.paymentMode?.type === 'required' && (
            <InlineMessage type="negative">
              {t(($) => $.required, {
                ns: 'validation'
              })}
            </InlineMessage>
          )}
        </DataBlock>
      </span>

      <Iban required={ibanIsRequired} identifier="ibanNumber" />

      <TextField
        label={t(($) => $.details.simulation.address.street)}
        {...register('address.streetName', {
          required: t(($) => $.required, {
            ns: 'validation'
          })
        })}
        error={errors.address?.streetName?.message}
      />

      <div className={styles['double-column']}>
        <TextField
          label={t(($) => $.details.simulation.address.number)}
          {...register('address.streetNumber', {
            required: t(($) => $.required, {
              ns: 'validation'
            })
          })}
          error={errors.address?.streetNumber?.message}
        />

        <TextField label={t(($) => $.details.simulation.address.addition)} {...register('address.streetBox')} />
      </div>

      <TextField
        label={t(($) => $.details.simulation.address.postalCode)}
        {...register('address.postalCode', {
          required: t(($) => $.required, {
            ns: 'validation'
          }),
          validate: (val) => validatePostalCodeAndFetchTowns(Number(val))
        })}
        type="number"
        error={errors.address?.postalCode?.message}
        onChange={(e) => {
          const value = e.target.valueAsNumber
          setValue('address.postalCode', value, { shouldValidate: true })
          void validatePostalCodeAndFetchTowns(Number(value))
        }}
      />

      <Select
        label={t(($) => $.details.simulation.address.locality)}
        {...register('address.townName', {
          required: t(($) => $.required, {
            ns: 'validation'
          })
        })}
        onChange={(e) => handleTownSelectChange(e.target.value)}
        value={watchTownName}
        error={errors.address?.townName?.message}
      >
        <option disabled>
          {!watch('address.postalCode')
            ? ''
            : t(($) => $.defaultPlaceholders.select, {
                ns: 'common'
              })}
        </option>
        {selectableTowns.map((town) => {
          return (
            <option key={town.townCode} value={town.townName}>
              {town.townName}
            </option>
          )
        })}
      </Select>
    </Form>
  )
}

export default EditSimulationDetails
